import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { api, fmt } from '../api';
import { SkeletonTable } from '../components/Skeleton';

type Row = {
  mid: string;
  name: string;
  active: number;
  start_date: string | null;
  term_days: number;
  deposit_amount: number;
  purchase_amount: number;
  purchase_not_applicable?: number;  // 문서 09-01 §9
  fee_rate: number;
  category: string;
  ceo_name: string | null;
  contact_phone: string | null;
  business_no: string | null;
  payout_method: 'bank' | 'wallet' | null;
  payout_bank: string | null;
  payout_account: string | null;
  people_id: string | null;
  created_at: string;
  tx_count: number;
  gross_total: number;
  first_trx_date: string | null;
  last_trx_date: string | null;
};

type SortKey = 'gross_total' | 'tx_count' | 'last_trx_date' | 'name';
type BucketKey = 'auto_gen' | 'term' | 'purchase' | 'purchase_na' | 'payout' | 'fee_rate';

// 정산 활성화에 필요한 필수 항목이 어떤 것이 빠졌는지 사람이 읽기 쉬운 목록으로 반환.
// 문서 09-01 §9: 매입액 미해당 플래그가 켜지면 "매입액 미입력"에서 제외 (별도 뱃지로만 표시).
function missingFields(r: Row): string[] {
  const out: string[] = [];
  if (!(Number(r.term_days) > 0)) out.push('기간 미입력');
  if (Number(r.purchase_not_applicable ?? 0) === 1) out.push('매입액 미해당');
  else if (!(Number(r.purchase_amount) > 0)) out.push('매입액 미입력');
  const method = r.payout_method ?? 'bank';
  if (method === 'bank') {
    if (!r.payout_bank || !r.payout_account) out.push('정산계좌 미등록');
  } else if (method === 'wallet') {
    if (!r.people_id) out.push('피플 ID 미입력');
  }
  if (!(Number(r.fee_rate) > 0)) out.push('수수료율 미입력');
  return out;
}

// 문서 09-01 §10: 카드용 버킷 분류 (한 가맹점이 여러 조건 동시 포함 가능).
function inBucket(r: Row, key: BucketKey): boolean {
  switch (key) {
    case 'auto_gen': return r.active === 0 && !r.start_date;
    case 'term': return !(Number(r.term_days) > 0);
    case 'purchase': return Number(r.purchase_not_applicable ?? 0) === 0 && !(Number(r.purchase_amount) > 0);
    case 'purchase_na': return Number(r.purchase_not_applicable ?? 0) === 1;
    case 'payout': {
      const m = r.payout_method ?? 'bank';
      if (m === 'bank') return !r.payout_bank || !r.payout_account;
      return !r.people_id;
    }
    case 'fee_rate': return !(Number(r.fee_rate) > 0);
  }
}

export default function PendingMerchants() {
  const [data, setData] = useState<Row[]>([]);
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('gross_total');
  const [bucket, setBucket] = useState<BucketKey | null>(null);  // 문서 09-01 §10
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api<{ data: Row[] }>('GET', '/merchants/pending-info');
      setData(r.data ?? []);
    } catch (e: any) {
      setErr(e?.message ?? '조회 실패');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = data;
    if (bucket) arr = arr.filter(r => inBucket(r, bucket));
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const digits = s.replace(/\D/g, '');
      arr = arr.filter(m => {
        const bnoDigits = (m.business_no ?? '').replace(/\D/g, '');
        return m.mid.toLowerCase().includes(s)
            || m.name.toLowerCase().includes(s)
            || (m.ceo_name?.toLowerCase().includes(s) ?? false)
            || (m.business_no?.toLowerCase().includes(s) ?? false)
            || (digits.length >= 3 && bnoDigits.includes(digits));
      });
    }
    const sorted = [...arr];
    sorted.sort((a, b) => {
      if (sortKey === 'name') return (a.name ?? '').localeCompare(b.name ?? '', 'ko');
      if (sortKey === 'last_trx_date') return (b.last_trx_date ?? '').localeCompare(a.last_trx_date ?? '');
      const av = Number((a as any)[sortKey] ?? 0);
      const bv = Number((b as any)[sortKey] ?? 0);
      return bv - av;
    });
    return sorted;
  }, [data, q, sortKey, bucket]);

  // 카드용 건수 (전체 데이터 기준)
  const bucketCounts: Record<BucketKey, number> = {
    auto_gen: data.filter(r => inBucket(r, 'auto_gen')).length,
    term: data.filter(r => inBucket(r, 'term')).length,
    purchase: data.filter(r => inBucket(r, 'purchase')).length,
    purchase_na: data.filter(r => inBucket(r, 'purchase_na')).length,
    payout: data.filter(r => inBucket(r, 'payout')).length,
    fee_rate: data.filter(r => inBucket(r, 'fee_rate')).length,
  };
  const BUCKET_LABEL: Record<BucketKey, string> = {
    auto_gen: '자동 생성 정보 미입력',
    term: '기간 미입력',
    purchase: '매입액 미입력',
    purchase_na: '매입액 미해당',
    payout: '정산계좌 미등록',
    fee_rate: '수수료율 미입력',
  };

  const totalGross = filtered.reduce((s, r) => s + (r.gross_total || 0), 0);
  const totalTx = filtered.reduce((s, r) => s + (r.tx_count || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>결제가맹점 추가</h2>
        <span style={{
          padding: '4px 12px', borderRadius: 999,
          background: data.length > 0 ? '#fef3c7' : '#dcfce7',
          color: data.length > 0 ? '#854d0e' : '#166534',
          fontSize: 13, fontWeight: 700,
        }} title="결제 수신내역은 있으나 계약·정산정보 등록·보완이 필요한 가맹점">
          결제가맹점 추가 대상 <b>{data.length.toLocaleString('ko-KR')}건</b>
          {filtered.length !== data.length && (
            <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 6 }}>(검색 결과 {filtered.length}건)</span>
          )}
        </span>
      </div>

      <div className="card" style={{ padding: '16px 20px', marginTop: 12 }}>
        <div className="callout" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
          <Info size={16} style={{ color: 'var(--brand-700)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>
            <b>결제정보는 수신되었으나 계약·정산정보가 미등록된 가맹점 목록</b>입니다.
            PG 결제 수신내역이 도착하면 시스템이 골격만 자동 생성(active=0)해 두며,
            운영자가 매입액·기간·정산계좌 등을 채워 넣어야 정산 화면에 노출됩니다.
            <br />
            <b>가맹점명</b>을 클릭하면 가맹점 수정 화면으로 이동해 정보를 보완할 수 있고,
            <b>[결제내역 보기]</b>로 실제 수신된 결제 원장을 즉시 확인할 수 있습니다.
            <br />
            ※ 운영자가 이미 등록(active=1)했지만 결제 수신내역이 한 번도 안 들어온 가맹점은
            <b>가맹점 관리</b> 화면에서 "결제 수신내역 없음" 칩으로 따로 표시됩니다.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <input type="text" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="MID · 가맹점명 · 대표자명 · 사업자등록번호 검색"
                 style={{ flex: 1, minWidth: 260, padding: '8px 10px' }} />
          <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>정렬</label>
          <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}
                  style={{ padding: '6px 8px' }}>
            <option value="gross_total">누적 매출액 높은 순</option>
            <option value="tx_count">누적 거래수 많은 순</option>
            <option value="last_trx_date">최근 거래일 최신 순</option>
            <option value="name">가맹점명 가나다순</option>
          </select>
          <button onClick={load} style={{
            padding: '8px 14px', background: '#fff', border: '1px solid var(--line)',
            borderRadius: 8, fontSize: 13,
          }}>새로고침</button>
        </div>
        {err && <div className="error">{err}</div>}

        {/* 문서 09-01 §10: 항목별 요약 카드 (클릭 시 해당 조건으로 필터) */}
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          {(Object.keys(BUCKET_LABEL) as BucketKey[]).map(k => {
            const active = bucket === k;
            const cnt = bucketCounts[k];
            return (
              <button key={k}
                      onClick={() => setBucket(active ? null : k)}
                      style={{
                        padding: '10px 12px', border: '1px solid var(--line)',
                        borderRadius: 8, background: active ? '#fef3c7' : '#fff',
                        cursor: 'pointer', textAlign: 'left',
                        outline: active ? '2px solid #d97706' : 'none',
                      }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{BUCKET_LABEL[k]}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: cnt > 0 ? '#c2410c' : 'var(--text-muted)' }}>
                  {cnt.toLocaleString('ko-KR')}건
                </div>
              </button>
            );
          })}
          {bucket && (
            <button onClick={() => setBucket(null)}
                    style={{ padding: '10px 12px', border: '1px dashed var(--line)', borderRadius: 8, background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}>
              필터 해제 (전체 {data.length}건)
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={5} cols={6} showHeader />
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-icon">✅</div>
          <h4>정보 미입력 가맹점이 없습니다</h4>
          <p>모든 결제 수신내역의 가맹점 정보가 등록되어 있습니다.</p>
        </div>
      ) : (
        <>
          <div className="summary" style={{ marginBottom: 14 }}>
            <div className="stat" style={{ borderLeft: '4px solid #d97706' }}>
              <div className="label">정보 미입력 가맹점 수</div>
              <div className="val" style={{ color: '#854d0e' }}>{filtered.length.toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat"><div className="label">누적 거래수</div><div className="val">{totalTx.toLocaleString('ko-KR')}</div></div>
            <div className="stat"><div className="label">누적 매출액</div><div className="val">{fmt.won(totalGross)}</div></div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrap" style={{ maxHeight: '70vh', border: 0 }}>
              <table>
                <thead><tr>
                  <th className="nowrap">MID</th>
                  <th>가맹점명</th>
                  <th className="nowrap">대표자</th>
                  <th className="nowrap">상태 · 미입력 항목</th>
                  <th className="num nowrap">누적 거래수</th>
                  <th className="num nowrap">누적 매출액</th>
                  <th className="nowrap">최근 거래일</th>
                  <th className="nowrap" style={{ width: 1 }}>작업</th>
                </tr></thead>
                <tbody>
                  {filtered.map(r => {
                    const missing = missingFields(r);
                    return (
                      <tr key={r.mid}>
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{r.mid}</td>
                        <td>
                          <a onClick={(e) => { e.preventDefault(); navigate(`/merchants/${encodeURIComponent(r.mid)}`); }}
                             style={{ fontWeight: 600, cursor: 'pointer' }}>
                            {r.name}
                          </a>
                          {r.business_no && (
                            <div className="muted" style={{ fontSize: 11 }}>사업자 {r.business_no}</div>
                          )}
                        </td>
                        <td className="nowrap" style={{ color: r.ceo_name ? undefined : 'var(--muted)' }}>{r.ceo_name ?? '-'}</td>
                        <td className="nowrap">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            {r.active === 0 && (
                              <span style={{ background: '#fef3c7', color: '#854d0e', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}
                                    title="결제 수신으로 자동 생성된 가맹점 골격. 정보 입력 후 활성화됩니다.">
                                자동 생성 · 정보 미입력
                              </span>
                            )}
                            {missing.map(m => (
                              <span key={m} style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                {m}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="num nowrap">{r.tx_count.toLocaleString('ko-KR')}</td>
                        <td className="num nowrap" style={{ fontWeight: 600 }}>{fmt.won(r.gross_total)}</td>
                        <td className="nowrap" style={{ color: 'var(--muted)' }}>{r.last_trx_date ?? '-'}</td>
                        <td className="nowrap">
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/notifications?mcht_id=${encodeURIComponent(r.mid)}`); }}
                                    style={{
                                      padding: '4px 10px', background: '#fff', color: 'var(--brand-700)',
                                      border: '1px solid var(--border)', borderRadius: 6, fontSize: 12,
                                    }}
                                    title="이 가맹점으로 수신된 결제 원장 보기">
                              결제내역 보기
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/merchants/${encodeURIComponent(r.mid)}`); }} style={{
                              padding: '4px 12px', background: 'var(--gray-900)', color: '#fff',
                              border: '1px solid var(--gray-900)',
                              borderRadius: 6, fontSize: 12, fontWeight: 600,
                              boxShadow: '0 2px 6px -2px rgba(20,30,25,.25)',
                            }}
                              title="매입액·기간·정산계좌 등 가맹점 정보 입력 화면으로 이동">
                              정보 입력
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
